/**
 * 会话数据库操作
 * 提供会话的增删改查功能
 */

import { dbManager } from './DatabaseManager'
import { isStrangerConversation } from '@/utils'
import { omit, chunk, keyBy } from 'lodash'

/**
 * 会话列表数据库操作对象
 *
 * 为什么所有方法都是 const db = dbManager.getDatabase() 而不直接把 db 保存起来？
 * 1. 防御性编程
 *    - 数据库被关闭后，返回 null
 *    - 避免访问无效的数据库实例
 * 2. 支持用户切换
 *    - 用户切换时数据库实例会变化
 * 3. 统一错误处理
 *    - 统一检查数据库是否已初始化
 */
/**
   * 批量保存会话列表到 IndexedDB
   * @param {Array} conversations - 会话列表
   */
export async function saveConversations(conversations) {
  console.log('saveConversations', conversations)
  if (!conversations || conversations.length === 0) {
    return
  }

  const db = dbManager.getDatabase()
  if (!db) throw new Error('Database not initialized')

  // 为缺失字段设置默认值 + 计算陌生人状态
  const normalizedConversations = conversations.map(conv => {
    // 判断是否是陌生人会话
    const isStranger = isStrangerConversation(conv)

    return {
      ...conv,
      // 为缺失 rank 字段的会话设置默认值 0，rank 1 代表置顶
      rank: conv.rank !== undefined ? conv.rank : 0,
      // 新增陌生人状态字段
      is_stranger: isStranger
    }
  })

  await db.conversations.bulkPut(normalizedConversations)
}

/**
 * 从数据库中获取所有会话列表（按置顶和时间排序）
 * @returns {Promise<Array>}
 */
export async function getAllConversations() {
  const db = dbManager.getDatabase()
  if (!db) return []

  // 使用复合索引查询（置顶在前，最新消息在前）
  const conversations = await db.conversations
    .orderBy('[rank+last_msg_at]')
    .reverse()
    .toArray()

  return conversations
}

/**
 * 获取单个会话
 * @param {string} conversationId - 会话ID
 * @returns {Promise<Object|undefined>}
 */
export async function getConversation(conversationId) {
  const db = dbManager.getDatabase()
  if (!db) return undefined
  return await db.conversations.get(conversationId)
}

/**
 * 更新会话信息
 * @param {string} conversationId - 会话ID
 * @param {Object} updates - 更新的字段
 */
export async function updateConversation(conversationId, updates) {
  const db = dbManager.getDatabase()
  if (!db) throw new Error('Database not initialized')
  await db.conversations.update(conversationId, updates)
}

/**
 * 删除会话
 * @param {string} conversationId - 会话ID
 */
export async function deleteConversation(conversationId) {
  const db = dbManager.getDatabase()
  if (!db) throw new Error('Database not initialized')
  await db.conversations.delete(conversationId)
}

/**
 * 清空所有会话
 */
export async function clearAll() {
  const db = dbManager.getDatabase()
  if (!db) return
  await db.conversations.clear()
}

/**
 * 选择性更新会话列表（排除特定字段）
 * 用于从 API 刷新会话时，保留本地的某些实时字段
 *
 * 优化策略：
 * 1. 批量读取：使用 bulkGet 一次性读取多条数据
 * 2. 批量写入：使用 bulkPut 一次性写入多条数据
 * 3. 分批处理：每批处理 BATCH_SIZE 条，避免内存溢出
 *
 * @param {Array} conversations - API 返回的会话列表
 * @param {Array<string>} excludeFields - 排除字段列表
 */
export async function updateConversationsSelectively(conversations, excludeFields = []) {
  if (!conversations || conversations.length === 0) {
    return
  }

  const db = dbManager.getDatabase()
  if (!db) throw new Error('Database not initialized')

  const BATCH_SIZE = 50 // 每批处理 50 条，平衡性能和内存

  // 使用 lodash chunk 分批
  const batches = chunk(conversations, BATCH_SIZE)

  // 处理每个批次
  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i]

    // 批量获取现有会话
    const conversationIds = batch.map(c => c.conversation_id)
    const existingConversations = await db.conversations.bulkGet(conversationIds)

    // 构建 ID -> 会话 的映射，过滤掉不存在的会话
    const existingConvMap = keyBy(
      existingConversations.filter(Boolean),
      'conversation_id'
    )

    // 准备批量更新的数据
    const updatedConversations = batch.map((newConv) => {
      const existingConv = existingConvMap[newConv.conversation_id]

      if (!existingConv) {
        // 数据库中不存在：直接添加新会话（完整数据）
        return newConv
      }

      // 数据库中已存在：选择性合并，保留排除字段的值
      return { ...existingConv, ...omit(newConv, excludeFields) }
    })

    // 批量更新
    if (updatedConversations.length) {
      await db.conversations.bulkPut(updatedConversations)
    }
  }
}
